import * as React from 'react'
import styles from './BucketGroupSelector.pcss'
import Button from '@mui/material/Button'
import Popper from '@mui/material/Popper'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { useDispatch, useSelector } from 'react-redux'
import { bucketGroupsSelector } from 'services/post/selectors'
import Icon from '@mdi/react'
import { mdiMenuDown, mdiClose } from '@mdi/js'
import { BucketGroup, MAX_BUCKET_GROUP_NAME_LENGTH } from 'shared'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import { createBucketGroup, deleteBucketGroup, getBuckets } from 'services/post/actions'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import { overflowModifiers } from 'styles/popover'

const LABEL_EMPTY = 'Select group'

interface BucketGroupSelectorProps {
  selectedGroupId: string | null
  className?: string
  onSelectionChange: (groupId: string | null) => void
}

export function BucketGroupSelector({ selectedGroupId, className, onSelectionChange }: BucketGroupSelectorProps) {
  const groups = useSelector(bucketGroupsSelector)
  const [creatingGroup, setCreatingGroup] = React.useState(false)
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [createGroup, setCreateGroup] = React.useState({ name: '', active: false })
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null)
  const [filter, setFilter] = React.useState('')

  React.useEffect(() => {
    dispatch(getBuckets())
  }, [dispatch])

  const label = React.useMemo(() => {
    if (createGroup.active) {
      return LABEL_EMPTY
    }

    return selectedGroupId
      ? groups.find(group => group.id === selectedGroupId)?.name || LABEL_EMPTY
      : LABEL_EMPTY
  }, [createGroup.active, groups, selectedGroupId])

  const deselect = () => {
    onSelectionChange(null)
    setAnchor(null)
    setFilter('')
    setCreateGroup({ name: '', active: false })
  }

  const openDropdown = (event: React.MouseEvent<HTMLElement>) => {
    setFilter('')
    if (groups.length === 0) {
      setCreateGroup({ name: '', active: !createGroup.active })
    } else {
      setAnchor(event.currentTarget)
    }

    const input = event.currentTarget.querySelector('input')
    setTimeout(() => {
      input?.focus()
      input?.setSelectionRange(0, 0)
    })
  }

  const closeDropdown = () => {
    setAnchor(null)
    setFilter('')
  }

  const onCreate = () => {
    const name = createGroup.name.trim()
    if (!name) {
      dispatch(message('Group name cannot be empty!'))
      return
    }
    if (name.length > MAX_BUCKET_GROUP_NAME_LENGTH) {
      dispatch(message(`Group name is too long! Maximum length is ${MAX_BUCKET_GROUP_NAME_LENGTH} characters.`))
      return
    }
    if (groups.find(group => group.name.toLowerCase() === name.toLowerCase())) {
      dispatch(message('Group with this name already exists!'))
      return
    }
    setCreatingGroup(true)
    dispatch(createBucketGroup({ name }))
      .unwrap()
      .then((g: BucketGroup) => {
        return dispatch(getBuckets()).unwrap().then(() => g.id)
      })
      .then(id => {
        onSelectedGroupChange(id)
      })
      .catch(() => {
        dispatch(message('Failed to create group!'))
      })
      .finally(() => {
        setCreatingGroup(false)
        setCreateGroup({ name: '', active: false })
      })
  }

  const onSelectedGroupChange = (groupId: string) => {
    onSelectionChange(groupId)
    const group = groups.find(g => g.id === groupId)
    if (group) {
      setFilter(group.name)
    }
    setCreateGroup({ name: '', active: false })
    setAnchor(null)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onCreate()
    }
  }

  const openCreateGroup = () => {
    setCreateGroup({ name: '', active: true })
    setAnchor(null)
    setFilter('')
  }

  const onAddGroupClick = () => {
    onSelectionChange(null)
    setAnchor(null)
    setFilter('')
    setCreateGroup({ name: '', active: true })
  }

  const deleteGroup = (g: BucketGroup) => {
    if (g.buckets.size > 0) {
      dispatch(message('Group is not empty! Please remove all buckets first.'))
      return Promise.resolve(false)
    }

    return dispatch(deleteBucketGroup(g.id)).unwrap().then(() => true)
  }

  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value)
  }

  const onInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.focus()
    e.stopPropagation()
    e.preventDefault()
  }

  const btnActiveClass = selectedGroupId !== null || createGroup.active ? styles.active : ''
  // eslint-disable-next-line no-magic-numbers
  const withFilter = groups.length > 10
  const btnEditableClass = withFilter && anchor ? styles.editable : ''
  const search = filter.trim().toLowerCase()
  const visibleGroups = search ? groups.filter(g => g.name.toLowerCase().includes(search)) : groups

  return (
    <div className={className}>
      <div className={styles.container}>
        <Button
          variant="text"
          className={`${styles.btn} ${selectedGroupId === null && !createGroup.active ? styles.active : ''}`}
          onClick={deselect}
        >
          No group
        </Button>
        <div
          className={`${styles.btn} ${styles['btn-dd']} ${btnActiveClass} ${btnEditableClass}`}
          onClick={openDropdown}
        >
          <span className={`${styles['btn-label']} text-ellipsis`}>{label}</span>
          <input
            type="text"
            className={styles['input-filter']}
            value={filter}
            disabled={!withFilter}
            placeholder="Filter groups..."
            onClick={onInputClick}
            onChange={onFilterChange}
          />
          <Icon path={mdiMenuDown} size="20px" />
        </div>
        <span role="button" className={styles['btn-add-new']} onClick={onAddGroupClick}>+ Add new group</span>
      </div>
      <Popper
        open={Boolean(anchor)}
        anchorEl={anchor}
        className={styles.popper}
        modifiers={overflowModifiers}
      >
        <ClickAwayListener onClickAway={closeDropdown}>
          <Paper className={styles['list-container']} elevation={5}>
            <ul>
              <li
                className={`${styles.item} ${selectedGroupId === null ? styles.selected : ''}`}
                onClick={openCreateGroup}
              >
                {LABEL_EMPTY}
              </li>
              {visibleGroups.map(group => (
                <BucketGroupListItem
                  key={group.id}
                  group={group}
                  selected={selectedGroupId === group.id}
                  onClick={onSelectedGroupChange}
                  onDelete={deleteGroup}
                />
              ))}
            </ul>
          </Paper>
        </ClickAwayListener>
      </Popper>
      {createGroup.active && (
        <div className={styles.actions}>
          <TextField
            type="text"
            placeholder="Add a name for your bucket group"
            value={createGroup.name}
            onChange={e => setCreateGroup({ ...createGroup, name: e.target.value })}
            onKeyDown={onKeyDown}
            className={styles.input}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={creatingGroup}
            startIcon={creatingGroup ? <CircularProgress size="16px" /> : null}
            onClick={onCreate}
          >
            {`Creat${creatingGroup ? 'ing' : 'e'} group${creatingGroup ? '...' : ''}`}
          </Button>
        </div>
      )}
    </div>
  )
}

type BucketGroupListItemProps = {
  group: BucketGroup,
  selected: boolean,
  onClick: (id: string) => void,
  onDelete: (g: BucketGroup) => Promise<boolean>
}

const BucketGroupListItem = ({ group, selected, onClick, onDelete }: BucketGroupListItemProps) => {
  const [isDeleting, setIsDeleting] = React.useState(false)
  const onClickHandler = () => {
    onClick(group.id)
  }

  const deleteGroup = (e: any) => {
    e.stopPropagation()
    onDelete(group).then(deleted => {
      if (!deleted) {
        setIsDeleting(false)
      }
    })
    setIsDeleting(true)
  }

  return (
    <li
      className={`${styles.item} ${selected ? styles.selected : ''} ${isDeleting ? styles.inactive : ''}`}
      title={group.name}
      onClick={onClickHandler}
    >
      <span className="text-ellipsis">{group.name}</span>
      <Tooltip title="Delete group">
        <div className={styles['btn-delete']} onClick={deleteGroup}>
          <Icon path={mdiClose} size="16px" />
        </div>
      </Tooltip>
    </li>
  )
}
